import { useMemo } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { leaveTypeColor, leaveTypeLabel } from '../../utils/leaveTypes.js';

/**
 * Der Abwesenheitskalender als Monatsraster.
 *
 * Vorher war das eine Liste plus eine zweite Liste, chronologisch über alle
 * Jahre - und damit genau die Darstellung, die die Frage nicht beantwortet,
 * wegen der man sie öffnet: wer ist nächste Woche da?
 *
 * Ein Raster beantwortet sie, weil es zwei Achsen hat. Wer senkrecht schaut,
 * sieht einen Tag; wer waagrecht schaut, sieht eine Person. Eine Liste kann
 * immer nur eins von beidem.
 *
 * Drei Dinge belegen einen Tag, und alle drei sind zu sehen: Abwesenheiten,
 * Feiertage und Firmenanlässe. Die beiden letzten lagen längst in der
 * Datenbank und wurden auf dieser Seite nie angezeigt.
 */

/** Alle Tage eines Monats als JJJJ-MM-TT, in UTC gerechnet. */
function tageDes(year, month) {
  const letzter = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const raus = [];
  for (let d = 1; d <= letzter; d += 1) {
    raus.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return raus;
}

/** ISO-Wochentag, 1 = Montag. In UTC - lokal wäre es in Mitteleuropa daneben. */
function wochentag(iso) {
  const wd = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return wd === 0 ? 7 : wd;
}

const WOCHENTAG_KEYS = [
  'weekday.short.mon',
  'weekday.short.tue',
  'weekday.short.wed',
  'weekday.short.thu',
  'weekday.short.fri',
  'weekday.short.sat',
  'weekday.short.sun',
];

export function LeaveCalendarGrid({ data, isLoading, onPick }) {
  const { t } = useTranslation();

  const tage = useMemo(
    () => (data ? tageDes(data.year, data.month) : []),
    [data?.year, data?.month],
  );

  /* Ein Nachschlagewerk statt einer Suche je Zelle. Bei 30 Personen und
     31 Tagen wären das sonst 930 Durchläufe durch die Antragsliste. */
  const belegung = useMemo(() => {
    const karte = new Map();
    for (const a of data?.absences || []) {
      let tag = a.startDate;
      while (tag <= a.endDate) {
        const halb =
          (tag === a.startDate && a.startHalfDay) || (tag === a.endDate && a.endHalfDay);
        karte.set(`${a.employeeId}|${tag}`, { ...a, halb });
        const nächster = new Date(`${tag}T00:00:00Z`).getTime() + 86_400_000;
        tag = new Date(nächster).toISOString().slice(0, 10);
      }
    }
    return karte;
  }, [data?.absences]);

  const feiertage = useMemo(() => {
    const karte = new Map();
    for (const h of data?.holidays || []) {
      karte.set(h.holidayDate, h);
    }
    return karte;
  }, [data?.holidays]);

  if (isLoading) {
    return <p className="muted">{t('hr.leave.calendar.loading')}</p>;
  }
  if (!data) {
    return <p className="muted">{t('hr.leave.calendar.empty')}</p>;
  }

  const anlässe = data.events || [];

  return (
    <>
      {/* Firmenanlässe stehen ÜBER dem Raster, nicht darin: sie betreffen
          alle, und eine Zeile je Person würde dieselbe Information
          dreissigmal wiederholen. */}
      {anlässe.length > 0 && (
        <ul className="cal-events">
          {anlässe.map((e) => (
            <li key={e.id}>
              <span className="cal-event-dot" />
              <strong>{e.title}</strong>
              <span className="muted text-xs">
                {e.startDate === e.endDate ? e.startDate : `${e.startDate} - ${e.endDate}`}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Eigener Scrollbereich: 31 Spalten passen auf keinen Bildschirm, und
          die Seite selbst darf dafür nicht waagrecht wandern. */}
      <div className="cal-wrap">
        <table className="cal-grid">
          <thead>
            <tr>
              <th className="cal-name-col">{t('hr.leave.col.employee')}</th>
              {tage.map((tag) => {
                const wd = wochentag(tag);
                const feiertag = feiertage.get(tag);
                return (
                  <th
                    key={tag}
                    className="cal-day-head"
                    data-weekend={wd >= 6 || undefined}
                    data-holiday={feiertag ? feiertag.kind : undefined}
                    title={feiertag ? feiertag.name : undefined}
                  >
                    <span className="cal-day-wd">{t(WOCHENTAG_KEYS[wd - 1])}</span>
                    <span className="cal-day-num">{Number(tag.slice(8))}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.employees.map((person) => (
              <tr key={person.id}>
                <th className="cal-name-col" scope="row">
                  {person.name}
                </th>
                {tage.map((tag) => {
                  const eintrag = belegung.get(`${person.id}|${tag}`);
                  const wd = wochentag(tag);
                  const feiertag = feiertage.get(tag);
                  return (
                    <td
                      key={tag}
                      className="cal-cell"
                      data-weekend={wd >= 6 || undefined}
                      data-holiday={feiertag ? feiertag.kind : undefined}
                    >
                      {eintrag && (
                        /* Offen sieht anders aus als genehmigt. Ohne diesen
                           Unterschied sähe eine Planung wie eine Zusage aus -
                           und der Genehmigende entschiede blind. */
                        <button
                          type="button"
                          className="cal-bar"
                          data-status={eintrag.status}
                          data-half={eintrag.halb || undefined}
                          style={{ '--bar': leaveTypeColor(eintrag.leaveTypeColor) }}
                          onClick={() => onPick?.(eintrag)}
                          title={`${person.name} · ${leaveTypeLabel(t, eintrag)} · ${t(
                            `status.${eintrag.status}`,
                          )}`}
                        >
                          <span className="sr-only">
                            {leaveTypeLabel(t, eintrag)} {t(`status.${eintrag.status}`)}
                          </span>
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ohne Legende ist ein Farbraster eine Ratearbeit. */}
      <div className="cal-legend">
        <span className="cal-legend-item">
          <span className="cal-bar" data-status="APPROVED" style={{ '--bar': 'var(--color-accent)' }} />
          {t('hr.leave.calendar.legend.approved')}
        </span>
        <span className="cal-legend-item">
          <span className="cal-bar" data-status="PENDING" style={{ '--bar': 'var(--color-accent)' }} />
          {t('hr.leave.calendar.legend.pending')}
        </span>
        <span className="cal-legend-item">
          <span className="cal-legend-swatch" data-holiday="PUBLIC" />
          {t('hr.holidays.kind.PUBLIC')}
        </span>
        <span className="cal-legend-item">
          <span className="cal-legend-swatch" data-holiday="COMPANY" />
          {t('hr.holidays.kind.COMPANY')}
        </span>
      </div>
    </>
  );
}
